import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Container, Row, Form, Button } from "react-bootstrap";
import BranchCreateConflictModal from "../components/DhlBranch/BranchCreateConflictModal";
import BranchEditConflictModal from "../components/DhlBranch/BranchEditConflictModal";
import BranchTerminalChangeModal from "../components/DhlBranch/BranchTerminalChangeModal";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import api from "../services/httpClient";
import "../assets/styles/dhlBranch.scss";

const DhlBranchForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    ex: "",
    dr: "",
    openTimeFrom: "",
    openTimeTo: "",
    postcodes: "",
  });

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [conflictData, setConflictData] = useState([]);
  const [removedPostcodes, setRemovedPostcodes] = useState([]);
  const [unchangedCount, setUnchangedCount] = useState(0);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [terminalChangeData, setTerminalChangeData] = useState(null);
  // Przechowuje ostatni payload (conflict mode, daty) potrzebny do ponownego wysłania z potwierdzeniem
  const [pendingPayload, setPendingPayload] = useState(null);

  // Pobranie danych oddziału przy edycji
  useEffect(() => {
    if (!isEditMode) return;
    const fetchBranch = async () => {
      try {
        const response = await api.get(`/Branch/${id}`);
        const result = response.data?.data;
        if (result?.rows && result.rows.length > 0) {
          const branch = result.rows[0];
          // Konwersja JSON array kodów na tekst (jeden kod na linię)
          let postcodesText = "";
          if (branch.postcodes) {
            try {
              const arr = JSON.parse(branch.postcodes);
              postcodesText = Array.isArray(arr) ? arr.join("\n") : branch.postcodes;
            } catch {
              postcodesText = branch.postcodes;
            }
          }
          setFormData({
            ex: branch.ex || "",
            dr: branch.dr || "",
            openTimeFrom: branch.openTimeFrom || "",
            openTimeTo: branch.openTimeTo || "",
            postcodes: postcodesText,
          });
        }
      } catch (error) {
        console.error("Error fetching branch for edit:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBranch();
  }, [id, isEditMode]);

  const handleChange = (field, value) => {
    // Auto-uppercase dla EX i DR
    if (field === "ex" || field === "dr") {
      value = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFormData({
      ex: "",
      dr: "",
      openTimeFrom: "",
      openTimeTo: "",
      postcodes: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const requestBody = {
        data: {
          ex: formData.ex,
          dr: formData.dr,
          openTimeFrom: formData.openTimeFrom,
          openTimeTo: formData.openTimeTo,
          postcodes: formData.postcodes,
        },
      };

      if (isEditMode) {
        await api.put(`/Branch/${id}`, requestBody);
      } else {
        await api.post(`/Branch`, requestBody);
      }

      navigate("/administration/dhl-branches");
    } catch (error) {
      console.error("Error saving branch:", error);

      // Obsługa konfliktu kodów pocztowych (409 Conflict)
      if (
        error.response?.status === 409 &&
        error.response?.data?.data?.hasConflicts
      ) {
        setConflictData(error.response.data.data.conflictItems || []);
        setRemovedPostcodes(error.response.data.data.removedPostcodes || []);
        setUnchangedCount(error.response.data.data.unchangedCount || 0);
        setShowConflictModal(true);
      } else if (
        error.response?.status === 449 &&
        error.response?.data?.data?.hasTerminalChanges
      ) {
        // Zmiana terminali wymaga potwierdzenia propagacji
        setTerminalChangeData(error.response.data.data);
        setPendingPayload(null); // brak conflict mode — zwykły submit
        setShowTerminalModal(true);
      } else {
        const msg =
          error.response?.data?.data?.message ||
          error.response?.data?.message ||
          "Błąd podczas zapisu.";
        alert(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Obsługa potwierdzenia z modalu konfliktów
  const handleConflictConfirm = async (mode, executionDate, rollbackDate) => {
    setSubmitting(true);
    try {
      const requestBody = {
        data: {
          ex: formData.ex,
          dr: formData.dr,
          openTimeFrom: formData.openTimeFrom,
          openTimeTo: formData.openTimeTo,
          postcodes: formData.postcodes,
          postcodeConflictMode: mode === "TransferAll" ? 1 : mode === "OnlyNew" ? 2 : 3,
          executionDate: executionDate || null,
          rollbackDate: rollbackDate || null,
        },
      };

      if (isEditMode) {
        await api.put(`/Branch/${id}`, requestBody);
      } else {
        await api.post(`/Branch`, requestBody);
      }
      setShowConflictModal(false);
      navigate("/administration/dhl-branches");
    } catch (error) {
      console.error("Error saving branch with conflict mode:", error);

      // Obsługa 449 po potwierdzeniu konfliktu kodów — terminale też się zmieniły
      if (
        error.response?.status === 449 &&
        error.response?.data?.data?.hasTerminalChanges
      ) {
        setShowConflictModal(false);
        setTerminalChangeData(error.response.data.data);
        setPendingPayload({ mode, executionDate, rollbackDate });
        setShowTerminalModal(true);
        return;
      }

      const msg =
        error.response?.data?.data?.message ||
        error.response?.data?.message ||
        "Błąd podczas zapisu.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Obsługa potwierdzenia propagacji terminali (teraz lub zaplanowane)
  const handleTerminalConfirm = async (terminalExecutionDate) => {
    setSubmitting(true);
    try {
      const requestBody = {
        data: {
          ex: formData.ex,
          dr: formData.dr,
          openTimeFrom: formData.openTimeFrom,
          openTimeTo: formData.openTimeTo,
          postcodes: formData.postcodes,
          terminalPropagationConfirmed: true,
          terminalExecutionDate: terminalExecutionDate || null,
          // Jeśli wcześniej zatwierdzono conflict mode — dołącz te dane
          ...(pendingPayload ? {
            postcodeConflictMode: pendingPayload.mode === "TransferAll" ? 1 : pendingPayload.mode === "OnlyNew" ? 2 : 3,
            executionDate: pendingPayload.executionDate || null,
            rollbackDate: pendingPayload.rollbackDate || null,
          } : {}),
        },
      };

      await api.put(`/Branch/${id}`, requestBody);
      setShowTerminalModal(false);
      navigate("/administration/dhl-branches");
    } catch (error) {
      console.error("Error saving branch with terminal confirmation:", error);
      const msg =
        error.response?.data?.data?.message ||
        error.response?.data?.message ||
        "Błąd podczas zapisu.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="dhl-branch-container">Ładowanie...</div>;

  return (
    <div className="dhl-branch-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={AdministrationIcon}
              alt="Branch Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              {isEditMode ? "Edycja oddziału DHL" : "Dodawanie oddziału DHL"}
            </h3>
          </Col>
        </Row>

        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="ex">
                <Form.Label>Terminal EX *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="np. W01"
                  value={formData.ex}
                  onChange={(e) => handleChange("ex", e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="dr">
                <Form.Label>Terminal DR *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="np. W01"
                  value={formData.dr}
                  onChange={(e) => handleChange("dr", e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="openTimeFrom">
                <Form.Label>Otwarty od *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="HH:mm"
                  value={formData.openTimeFrom}
                  onChange={(e) => handleChange("openTimeFrom", e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="openTimeTo">
                <Form.Label>Otwarty do *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="HH:mm"
                  value={formData.openTimeTo}
                  onChange={(e) => handleChange("openTimeTo", e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="postcodes">
                <Form.Label>Kody pocztowe</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={12}
                  placeholder={"Jeden kod na linię (znak enter) chyba, że wymieniane po przecinku, np.:\n00-001, 00-002, 00-003\nlub podając zakres: 00-101*00-205"}
                  value={formData.postcodes}
                  onChange={(e) => handleChange("postcodes", e.target.value)}
                />
                <Form.Text className="text-muted">
                  Aby przypisać zakres kodów pocztowych do jednostki podaj kody w postaci
                  00-101*00-205, system automatycznie doda listę kodów pocztowych do danej
                  jednostki.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col>
              <Button variant="secondary" className="me-2" onClick={handleReset}>
                Reset
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? "Zapisywanie..." : "Zapisz"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>

      {isEditMode ? (
        <BranchEditConflictModal
          show={showConflictModal}
          onHide={() => setShowConflictModal(false)}
          onConfirm={handleConflictConfirm}
          conflictItems={conflictData}
          removedPostcodes={removedPostcodes}
          unchangedCount={unchangedCount}
          submitting={submitting}
        />
      ) : (
        <BranchCreateConflictModal
          show={showConflictModal}
          onHide={() => setShowConflictModal(false)}
          onConfirm={handleConflictConfirm}
          conflictItems={conflictData}
          submitting={submitting}
        />
      )}

      {/* Modal propagacji terminali EX/DR */}
      {terminalChangeData && (
        <BranchTerminalChangeModal
          show={showTerminalModal}
          onHide={() => setShowTerminalModal(false)}
          onConfirm={handleTerminalConfirm}
          affectedCount={terminalChangeData.terminalChangeAffectedCount}
          oldEx={terminalChangeData.oldEx}
          oldDr={terminalChangeData.oldDr}
          newEx={terminalChangeData.newEx}
          newDr={terminalChangeData.newDr}
          submitting={submitting}
        />
      )}
    </div>
  );
};

export default DhlBranchForm;
